import React from 'react';
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from '@remix-run/react';
import type { LinksFunction, HeadersFunction } from '@remix-run/node';
import { Analytics } from '@vercel/analytics/remix';
import { Layout as AppLayout } from '~/components/layout/layout';
import { ColorSchemeScript, ThemeProvider } from '~/components/theme/theme-provider';
import { AudioProvider } from '~/contexts/AudioContext';
import { securityHeaders } from '~/lib/security.server';
import stylesheet from './styles/tailwind.css?url';

export const links: LinksFunction = () => [
  // DNS prefetch and preconnect for external resources
  { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
  { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  // Preconnect to analytics and CDN (Vercel Analytics)
  { rel: 'dns-prefetch', href: 'https://vitals.vercel-insights.com' },
  { rel: 'preconnect', href: 'https://vitals.vercel-insights.com' },
  // Font loading with display=swap for better performance
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap',
  },
  // Favicon and app icons
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  { rel: 'icon', type: 'image/png', href: '/favicon.png' },
  { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
  // PWA manifest
  { rel: 'manifest', href: '/manifest.json' },
  // Main stylesheet
  { rel: 'stylesheet', href: stylesheet },
];

export const headers: HeadersFunction = securityHeaders;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="scroll-smooth overflow-x-hidden" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <ColorSchemeScript />
      </head>
      <body className="min-h-screen bg-background font-body text-foreground antialiased transition-colors duration-300">
        <ThemeProvider>
          <AudioProvider>
            <AppLayout>{children}</AppLayout>
          </AudioProvider>
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
        <Analytics />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary() {
  const error = useRouteError();

  // Don't wrap in Layout again - we're already inside the root Layout
  if (isRouteErrorResponse(error)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-2xl space-y-6 px-4 text-center">
          <h1 className="text-6xl font-bold text-foreground">{error.status}</h1>
          <p className="text-xl text-muted-foreground">
            {error.status === 404 ? "Page not found" : error.statusText || "An error occurred"}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md bg-lumora-purple px-4 py-2 text-sm font-semibold text-white shadow hover:bg-lumora-purple/90 focus:outline-none focus:ring-2 focus:ring-lumora-purple focus:ring-offset-2"
            >
              Return home
            </Link>
            <Link
              to="/search"
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
            >
              Search the site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Generic error fallback
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="max-w-2xl space-y-6 px-4 text-center">
        <h1 className="text-5xl font-bold text-foreground">Unexpected Error</h1>
        <p className="text-lg text-muted-foreground">
          An unexpected error occurred. Please try refreshing the page.
        </p>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-md bg-lumora-purple px-4 py-2 text-sm font-semibold text-white shadow hover:bg-lumora-purple/90"
        >
          Return home
        </Link>
      </div>
    </div>
  );
}
